import type React from "react";
import type { PanelEntityType } from "@/v2/stores/panelStore";
import { BatteryDisplay } from "@/v2/ui/primitives/battery-display";
import { StatusBadge } from "@/v2/ui/primitives/status-badge";
import {
  BooleanCell,
  CodeCell,
  CurrencyCell,
  DateCell,
  EmailCell,
  EntityLinkCell,
  NumericCell,
  PhoneCell,
  StatusCell,
  TextCell,
} from "./column-cells";
import type { ResponsiveDataViewColumn } from "./ResponsiveDataView";

/**
 * Column Builder - A fluent API for building table columns with pre-built cell renderers
 *
 * @example
 * ```tsx
 * const columns = columnBuilder<VehicleListItem>()
 *   .text('name', { header: 'Name', getValue: (row) => row.name, fontWeight: 'medium', truncate: 140 })
 *   .text('code', { header: 'Code', getValue: (row) => row.code, className: 'tw-text-muted-foreground' })
 *   .entityLink('dock', { header: 'Dock', getCode: (row) => row.dock?.code, basePath: '/v2/dashboard/docks' })
 *   .battery('lockBattery', { header: 'Lock Battery', getValue: (row) => row.lock?.battery })
 *   .status('status', { header: 'Status', getValue: (row) => row.status, formatStatus })
 *   .custom('actions', { header: '', cell: (row) => <ActionMenu row={row} /> })
 *   .build()
 * ```
 */

type CellRenderer<T> = (row: T) => React.ReactNode;

interface BaseConfig {
  header: string;
  width?: number | string;
  hideOnMobile?: boolean;
  stopPropagation?: boolean;
}

interface TextConfig<T> extends BaseConfig {
  getValue: (row: T) => string | number | undefined | null;
  truncate?: number;
  className?: string;
  fontWeight?: "normal" | "medium" | "semibold" | "bold";
}

interface StatusConfig<T> extends BaseConfig {
  getValue: (row: T) => string | undefined | null;
  formatStatus?: (status: string | undefined) => string;
}

interface DateConfig<T> extends BaseConfig {
  getValue: (row: T) => string | Date | undefined | null;
  formatDate: (date: string | undefined | null) => string;
}

interface EntityLinkConfig<T> extends BaseConfig {
  getCode: (row: T) => string | number | undefined | null;
  getName?: (row: T) => string | undefined | null;
  basePath: string;
  truncate?: number;
  entityType?: PanelEntityType;
}

interface BatteryConfig<T> extends BaseConfig {
  getValue: (row: T) => number | undefined | null;
}

interface LockStatusConfig<T> extends BaseConfig {
  getValue: (row: T) => boolean | undefined | null;
}

interface NumericConfig<T> extends BaseConfig {
  getValue: (row: T) => number | undefined | null;
  format?: (value: number) => string;
  align?: "left" | "center" | "right";
}

interface EmailConfig<T> extends BaseConfig {
  getValue: (row: T) => string | undefined | null;
  truncate?: number;
}

interface PhoneConfig<T> extends BaseConfig {
  getValue: (row: T) => string | number | undefined | null;
  truncate?: number;
}

interface BooleanConfig<T> extends BaseConfig {
  getValue: (row: T) => boolean | undefined | null;
}

interface CurrencyConfig<T> extends BaseConfig {
  getAmount: (row: T) => number | undefined | null;
  getCurrency?: (row: T) => string | undefined | null;
  locale?: string;
}

interface CodeConfig<T> extends BaseConfig {
  getValue: (row: T) => string | number | undefined | null;
  truncate?: number;
}

interface CustomConfig<T> extends BaseConfig {
  cell: CellRenderer<T>;
}

class ColumnBuilder<T> {
  private columns: ResponsiveDataViewColumn<T>[] = [];

  /** Text column with optional truncation and styling */
  text(id: string, config: TextConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <TextCell
          value={config.getValue(row)}
          truncate={config.truncate}
          className={config.className}
          fontWeight={config.fontWeight}
        />
      ),
    });
    return this;
  }

  /** Status column with colored dot */
  status(id: string, config: StatusConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <StatusCell
          status={config.getValue(row)}
          formatStatus={config.formatStatus}
        />
      ),
    });
    return this;
  }

  /** Date column with formatting */
  date(id: string, config: DateConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => {
        const value = config.getValue(row);
        const dateStr = value instanceof Date ? value.toISOString() : value;
        return <DateCell date={dateStr} formatDate={config.formatDate} />;
      },
    });
    return this;
  }

  /** Entity link column (links to another page with search param) */
  entityLink(id: string, config: EntityLinkConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation ?? true,
      cell: (row) => (
        <EntityLinkCell
          code={config.getCode(row)}
          name={config.getName?.(row)}
          basePath={config.basePath}
          truncate={config.truncate}
          entityType={config.entityType}
        />
      ),
    });
    return this;
  }

  /** Battery display column */
  battery(id: string, config: BatteryConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => <BatteryDisplay value={config.getValue(row)} />,
    });
    return this;
  }

  /** Lock status badge column */
  lockStatus(id: string, config: LockStatusConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <StatusBadge status={config.getValue(row)} variant="lock" />
      ),
    });
    return this;
  }

  /** Numeric column with optional formatting */
  numeric(id: string, config: NumericConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <NumericCell
          value={config.getValue(row)}
          format={config.format}
          align={config.align}
        />
      ),
    });
    return this;
  }

  /** Email column with mailto link */
  email(id: string, config: EmailConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <EmailCell email={config.getValue(row)} truncate={config.truncate} />
      ),
    });
    return this;
  }

  /** Phone column with tel link */
  phone(id: string, config: PhoneConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <PhoneCell phone={config.getValue(row)} truncate={config.truncate} />
      ),
    });
    return this;
  }

  /** Boolean column with yes/no */
  boolean(id: string, config: BooleanConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => <BooleanCell value={config.getValue(row)} />,
    });
    return this;
  }

  /** Currency column with amount + currency code */
  currency(id: string, config: CurrencyConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <CurrencyCell
          amount={config.getAmount(row)}
          currency={config.getCurrency ? config.getCurrency(row) : undefined}
          locale={config.locale}
        />
      ),
    });
    return this;
  }

  /** Code column with monospace pill */
  code(id: string, config: CodeConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: (row) => (
        <CodeCell value={config.getValue(row)} truncate={config.truncate} />
      ),
    });
    return this;
  }

  /** Custom column with full control */
  custom(id: string, config: CustomConfig<T>): this {
    this.columns.push({
      id,
      header: config.header,
      width: config.width,
      hideOnMobile: config.hideOnMobile,
      stopPropagation: config.stopPropagation,
      cell: config.cell,
    });
    return this;
  }

  /** Add raw column definition */
  raw(column: ResponsiveDataViewColumn<T>): this {
    this.columns.push(column);
    return this;
  }

  /** Conditionally add columns */
  when(condition: boolean, fn: (builder: this) => void): this {
    if (condition) fn(this);
    return this;
  }

  /** Build final columns array */
  build(): ResponsiveDataViewColumn<T>[] {
    return this.columns;
  }
}

export function columnBuilder<T>(): ColumnBuilder<T> {
  return new ColumnBuilder<T>();
}
